import { useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Map sharps count → VexFlow key name (for key signature)
const SHARPS_TO_KEY = {
  "0":  "C",  "1":  "G",  "2":  "D",  "3":  "A",  "4":  "E",  "5":  "B",  "6":  "F#",
  "-1": "F",  "-2": "Bb", "-3": "Eb", "-4": "Ab", "-5": "Db", "-6": "Gb",
};

const LETTER_ORDER = ["C", "D", "E", "F", "G", "A", "B"];

// Assign octaves to an array of note names so notes ascend naturally
// A, B start at octave 3 to stay on the staff; others start at 4.
function assignOctaves(notes) {
  const rootLetter = notes[0][0];
  const rootIdx    = LETTER_ORDER.indexOf(rootLetter);
  let oct          = rootIdx >= 5 ? 3 : 4; // A,B → octave 3
  let prevIdx      = 100; // force first note to use starting octave
  return notes.map((note, i) => {
    const idx = LETTER_ORDER.indexOf(note[0]);
    if (i > 0 && idx <= prevIdx) oct++;
    prevIdx = idx;
    // VexFlow format: "bb/4", "c#/5" etc.
    const letter = note[0].toLowerCase();
    const acc    = note.slice(1); // "#", "b", or ""
    return `${letter}${acc}/${oct}`;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ScaleNotation({ scale, sharps }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !scale || scale.length === 0) return;

    // Clear any previous render
    el.innerHTML = "";

    const W   = Math.max(el.offsetWidth || 480, 320);
    const H   = 155;
    const keyName = SHARPS_TO_KEY[String(sharps)] ?? "C";

    try {
      const renderer = new Renderer(el, Renderer.Backends.SVG);
      renderer.resize(W, H);
      const ctx = renderer.getContext();
      ctx.setFont("Inter, Arial, sans-serif", 10);

      // Stave: vertically centered with room for ledger lines above and below
      const stave = new Stave(10, 40, W - 20);
      stave.addClef("treble");
      stave.addKeySignature(keyName);
      stave.setContext(ctx).draw();

      // Notes
      const vexKeys  = assignOctaves(scale);
      const duration = scale.length <= 8 ? "q" : "8";
      const staveNotes = vexKeys.map(k => new StaveNote({ keys: [k], duration }));

      // Voice – relaxed (setStrict false) so any number of beats works
      const voice = new Voice({ num_beats: scale.length, beat_value: duration === "q" ? 4 : 8 });
      voice.setStrict(false);
      voice.addTickables(staveNotes);

      // Let VexFlow figure out which accidentals to show relative to the key sig
      Accidental.applyAccidentals([voice], keyName);

      // Format & draw
      const usableWidth = W - 20 - (50 + Math.abs(sharps) * 12); // clef + key-sig width
      new Formatter().joinVoices([voice]).format([voice], Math.max(usableWidth, 120));
      voice.draw(ctx, stave);
    } catch (err) {
      console.warn("ScaleNotation render error:", err);
    }
  }, [scale, sharps]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        overflow: "hidden",
        borderRadius: 10,
        border: "1px solid #ebebeb",
        background: "#fff",
        padding: "2px 0 0",
        boxSizing: "border-box",
      }}
    />
  );
}
